/**
 * useMapLocalization — set localized aria labels for Leaflet controls.
 * 
 * Leaflet's default zoom/layers controls have English aria labels
 * ('Zoom in', 'Zoom out', 'Layers'). This hook patches those labels
 * with i18n translations after the map mounts.
 */
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import type { Control } from 'leaflet';
import { useLanguage } from '@/i18n';

export function useMapLocalization(): void {
  const map = useMap();
  const { t, language } = useLanguage();

  useEffect(() => {
    if (!map) return;

    // The attribution names the tile owner (Maanmittauslaitos, or OpenStreetMap
    // while its fallback grid is drawn); the map library's own prefix is not a
    // data source and is not shown.
    map.attributionControl?.setPrefix(false);

    const patchLayersControl = (): void => {
      const ariaLabels = t.common.leafletControls;
      const container = map.getContainer();

      const layersBtn = container.querySelector(
        '.leaflet-control-layers-toggle',
      ) as HTMLButtonElement | null;
      if (layersBtn) {
        layersBtn.setAttribute('aria-label', ariaLabels.layers);
        layersBtn.setAttribute('title', ariaLabels.layers);
      }
    };

    // Patch Leaflet zoom control aria labels
    const zoomControl = map.zoomControl as Control.Zoom | undefined;
    if (zoomControl) {
      const ariaLabels = t.common.leafletControls;

      // Locate the zoom-in and zoom-out buttons and update their aria-labels
      const container = zoomControl.getContainer();
      if (container) {
        const zoomInBtn = container.querySelector('.leaflet-control-zoom-in') as HTMLButtonElement | null;
        const zoomOutBtn = container.querySelector('.leaflet-control-zoom-out') as HTMLButtonElement | null;

        if (zoomInBtn) {
          zoomInBtn.setAttribute('aria-label', ariaLabels.zoomIn);
          zoomInBtn.setAttribute('title', ariaLabels.zoomIn);
        }
        if (zoomOutBtn) {
          zoomOutBtn.setAttribute('aria-label', ariaLabels.zoomOut);
          zoomOutBtn.setAttribute('title', ariaLabels.zoomOut);
        }
      }
    }

    // Patch Leaflet layers control aria labels
    patchLayersControl();

    const mapContainer = map.getContainer();
    const observer = new MutationObserver(() => patchLayersControl());
    observer.observe(mapContainer, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [map, language, t]);
}
